import { useEffect, useState } from 'react';
import { Section } from '~/components/layout';
import {
  FALLBACK_RATES,
  RECEIVE_CURRENCIES,
  SOURCE_CURRENCY,
  getRates,
  type PricingTier,
  type ReceiveCurrency,
} from '~/lib/quote';
import { ScrollStrip } from './ScrollStrip';

/**
 * Currency-rate strip under the send-money-home hero (Figma) — an instance of the
 * shared {@link ScrollStrip}. Each fixed-width item is an overlapping pair of
 * circular flags + "USD to TARGET" (17, semibold) with the LIVE per-unit rate
 * beneath (grey), in the same drag-scrollable / cropped track as the stats strip.
 *
 * Rates come from the same upstream as the calculator via the batch `getRates`
 * server fn (one round-trip for all pairs). Clicking an item selects that pair
 * and drives the calculator (state is owned by the send-money-home page). While
 * the live rates load — or if the upstream is unreachable — we show the static
 * {@link FALLBACK_RATES} so the strip never renders empty.
 */
const flagSrc = (code: string) => `/images/calculator/${code}.png`;

export function CurrencyRatesStrip({
  onSelect,
  plan = 'PRO',
}: {
  onSelect: (code: ReceiveCurrency) => void;
  plan?: PricingTier;
}) {
  // Live per-unit rates for the current plan. Keyed by plan so a plan change
  // shows the skeleton again until the fresh rates arrive; deriving `rates` from
  // the key keeps stale responses from showing without setState in the effect.
  const [result, setResult] = useState<{
    plan: PricingTier;
    map: Partial<Record<ReceiveCurrency, number>>;
  } | null>(null);
  const rates = result?.plan === plan ? result.map : null;

  useEffect(() => {
    let active = true;
    const p = plan;
    void getRates({ data: { pricingTier: p } })
      .then((entries) => {
        if (!active) return;
        const map: Partial<Record<ReceiveCurrency, number>> = {};
        for (const { code, rate } of entries) map[code] = rate;
        setResult({ plan: p, map });
      })
      .catch(() => {
        // Upstream unreachable — fall back to the static constants.
        if (active) setResult({ plan: p, map: { ...FALLBACK_RATES } });
      });
    return () => {
      active = false;
    };
  }, [plan]);

  return (
    <Section className="!pt-5 !pb-0">
      <ScrollStrip
        autoScroll
        items={RECEIVE_CURRENCIES.map((code) => ({ code }))}
        getKey={(pair) => pair.code}
        itemClassName="w-[220px]"
        renderItem={(pair) => {
          const rate = rates?.[pair.code] ?? FALLBACK_RATES[pair.code];
          return (
            <button
              type="button"
              aria-label={`Show ${SOURCE_CURRENCY} to ${pair.code} rate in the calculator`}
              onClick={() => onSelect(pair.code)}
              className="group flex h-full w-full items-center py-6 pr-4 pl-10 text-left">
              {/* Inner content scales down briefly on press for tap feedback; the
                  strip itself has no hover/selected styling. */}
              <span className="flex items-center gap-3 transition-transform duration-100 group-active:scale-95">
                {/* 40×40 wrapper with two 24×24 flags placed diagonally: the
                    source (USD) sits top-left and in front; the target flag sits
                    bottom-right and behind. */}
                <span className="relative block size-10 shrink-0">
                  <img
                    src={flagSrc(pair.code)}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    className="absolute right-0 bottom-0 size-6 rounded-full object-cover ring-2 ring-white"
                  />
                  <img
                    src={flagSrc(SOURCE_CURRENCY)}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    className="absolute top-0 left-0 z-10 size-6 rounded-full object-cover ring-2 ring-white"
                  />
                </span>
                <span className="flex flex-col gap-3">
                  <span className="text-body font-semibold whitespace-nowrap text-ink">
                    {SOURCE_CURRENCY} to {pair.code}
                  </span>
                  {rates ? (
                    <span className="text-body text-grey-600">{rate.toFixed(2)}</span>
                  ) : (
                    <span className="block h-5 w-16 rounded-md bg-grey-300" aria-hidden="true" />
                  )}
                </span>
              </span>
            </button>
          );
        }}
      />
    </Section>
  );
}
